import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Copy,
  Check,
  ExternalLink,
  MousePointer,
  Users,
  Calendar,
  TrendingUp,
  User,
  Smartphone,
  Globe,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  QrCode,
  MapPin,
} from 'lucide-react'
import { toast } from 'sonner'
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { StatsCard } from '../components/StatsCard'
import { WorldMap } from '../components/WorldMap'
import { linkTrackerApi } from '../api'
import type { AnalyticsURL } from '../types'

export function AnalyticsURLPage() {
  const { codigo } = useParams<{ codigo: string }>()
  const [data, setData] = useState<AnalyticsURL | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [clicsPage, setClicsPage] = useState(1)
  const [clicsPageSize, setClicsPageSize] = useState(10)
  // Filtro por país (dropdown). La distribución completa del mapa se conserva
  // aparte para poder seguir cambiando de país mientras se filtra.
  const [selectedPais, setSelectedPais] = useState<string | null>(null)
  const [paisDistribution, setPaisDistribution] = useState<{ nombre: string; clics: number }[]>([])

  useEffect(() => {
    if (codigo) cargarAnalytics()
  }, [codigo])

  useEffect(() => {
    if (!data?.url.habilitar_qr || !codigo) {
      setQrUrl(null)
      return
    }
    let revoked = false
    linkTrackerApi.obtenerQRBlob(codigo).then((blob) => {
      if (!revoked) setQrUrl(URL.createObjectURL(blob))
    }).catch(() => {})
    return () => {
      revoked = true
      if (qrUrl) URL.revokeObjectURL(qrUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.url.habilitar_qr, codigo])

  const cargarAnalytics = async (pais?: string) => {
    try {
      const result = await linkTrackerApi.getAnalytics(codigo!, pais)
      setData(result)
      // Capturar la distribución completa por país solo en la carga sin filtro
      if (!pais) setPaisDistribution(result.clics_por_pais)
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error al cargar analytics')
    } finally {
      setLoading(false)
    }
  }

  const handleFiltrarPais = (nombre: string | null) => {
    setSelectedPais(nombre)
    setClicsPage(1)
    cargarAnalytics(nombre ?? undefined)
  }

  const handleCopy = async (text: string, key: string) => {
    const success = await linkTrackerApi.copiarAlPortapapeles(text)
    if (success) {
      setCopied(key)
      setTimeout(() => setCopied(null), 2000)
      toast.success('Copiado al portapapeles')
    } else {
      toast.error('Error al copiar')
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      await linkTrackerApi.descargarExcel(codigo!)
      toast.success('Excel descargado')
    } catch (error) {
      toast.error('Error al descargar')
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/link-tracker/urls">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a URLs
          </Link>
        </Button>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <p>URL no encontrada</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/link-tracker/urls">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">
                {data.url.titulo || `URL /${data.url.codigo}`}
              </h1>
              <Badge className={data.url.activa ? 'badge-gradient hover:opacity-90' : 'bg-gray-100 text-gray-500 hover:bg-gray-100'}>
                {data.url.activa ? 'Activa' : 'Inactiva'}
              </Badge>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Info className="h-5 w-5" />
              </button>
              {showInfo && (
                <span className="text-sm text-muted-foreground font-mono truncate max-w-md animate-in slide-in-from-left-2 duration-200">
                  {data.url.url_destino}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button className="bg-teal-800 hover:bg-teal-900 text-white" onClick={handleDownload} disabled={downloading}>
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Descargando...' : 'Exportar Excel'}
          </Button>
          <Button variant="outline" className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800" asChild>
            <a href={data.url.url_destino} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Abrir destino
            </a>
          </Button>
        </div>
      </div>

      {/* URLs para copiar */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">URL Corta</Label>
              <div className="flex gap-2 mt-1">
                <Input value={data.url_corta} readOnly className="font-mono" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(data.url_corta, 'corta')}
                >
                  {copied === 'corta' ? (
                    <Check className="h-4 w-4 text-teal-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">URL con Tracking</Label>
              <div className="flex gap-2 mt-1">
                <Input value={data.url_con_tracking} readOnly className="font-mono text-xs" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(data.url_con_tracking, 'tracking')}
                >
                  {copied === 'tracking' ? (
                    <Check className="h-4 w-4 text-teal-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QR Code */}
      {data.url.habilitar_qr && (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Código QR</CardTitle>
            <div className="rounded-lg bg-teal-500/10 p-2">
              <QrCode className="h-4 w-4 text-teal-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="bg-white p-2 rounded-lg border border-gray-200">
                {qrUrl ? (
                  <img src={qrUrl} alt="QR Code" className="w-40 h-40" />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Escanéalo para redirigir a la URL de destino.
                </p>
                <Button
                  variant="outline"
                  className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                  onClick={() => linkTrackerApi.descargarQR(codigo!)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar QR
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Clics"
          value={data.total_clics}
          icon={MousePointer}
        />
        <StatsCard
          title="Clics Hoy"
          value={data.clics_hoy}
          icon={Calendar}
        />
        <StatsCard
          title="Últimos 7 días"
          value={data.clics_ultimos_7_dias}
          icon={TrendingUp}
        />
        <StatsCard
          title="Usuarios Únicos"
          value={data.usuarios_unicos}
          icon={Users}
        />
      </div>

      {/* Clics por Fecha */}
      {data.clics_por_dia.length > 0 && (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clics por Fecha</CardTitle>
            <div className="rounded-lg bg-teal-500/10 p-2">
              <TrendingUp className="h-4 w-4 text-teal-500" />
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={{ clics: { label: 'Clics', color: '#14b8a6' } } satisfies ChartConfig} className="h-48 w-full">
              <AreaChart
                accessibilityLayer
                data={data.clics_por_dia}
                margin={{ left: 12, right: 12 }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="fecha"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="line" />}
                />
                <Area
                  dataKey="clics"
                  type="natural"
                  fill="var(--color-clics)"
                  fillOpacity={0.4}
                  stroke="var(--color-clics)"
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Por Dispositivo, Navegador y Origen */}
      <div className={`grid grid-cols-1 gap-6 ${data.url.habilitar_qr ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Por Dispositivo</CardTitle>
            <div className="rounded-lg bg-teal-500/10 p-2">
              <Smartphone className="h-4 w-4 text-teal-500" />
            </div>
          </CardHeader>
          <CardContent>
            {data.clics_por_dispositivo.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Sin datos</p>
            ) : (
              <div className="space-y-4">
                {data.clics_por_dispositivo.map((item) => (
                  <div key={item.dispositivo} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{item.dispositivo}</span>
                      <span className="text-black dark:text-white">{item.clics} ({item.porcentaje.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-teal-500/10">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ background: 'linear-gradient(90deg, #14b8a6, #0f766e)', width: `${item.porcentaje}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Por Navegador</CardTitle>
            <div className="rounded-lg bg-teal-500/10 p-2">
              <Globe className="h-4 w-4 text-teal-500" />
            </div>
          </CardHeader>
          <CardContent>
            {data.clics_por_navegador.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Sin datos</p>
            ) : (
              <div className="space-y-4">
                {data.clics_por_navegador.map((item) => (
                  <div key={item.navegador} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{item.navegador}</span>
                      <span className="text-black dark:text-white">{item.clics} ({item.porcentaje.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-teal-500/10">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ background: 'linear-gradient(90deg, #14b8a6, #0f766e)', width: `${item.porcentaje}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Por Origen (solo si QR habilitado) */}
        {data.url.habilitar_qr && (
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Por Origen</CardTitle>
              <div className="rounded-lg bg-teal-500/10 p-2">
                <QrCode className="h-4 w-4 text-teal-500" />
              </div>
            </CardHeader>
            <CardContent>
              {data.clics_por_origen.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Sin datos</p>
              ) : (
                <div className="space-y-4">
                  {data.clics_por_origen.map((item) => (
                    <div key={item.origen} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{item.origen === 'qr' ? 'QR' : 'Link'}</span>
                        <span className="text-black dark:text-white">{item.clics} ({item.porcentaje.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-teal-500/10">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ background: 'linear-gradient(90deg, #14b8a6, #0f766e)', width: `${item.porcentaje}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Clics por Ubicación — Mapa + Tablas (solo si QR habilitado) */}
      {data.url.habilitar_qr && (data.clics_por_ciudad.length > 0 || data.clics_por_region.length > 0 || data.clics_por_pais.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mapa mundial por país */}
          <Card className="border border-gray-300 dark:border-gray-700 self-start">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mapa de Clics</CardTitle>
              <div className="rounded-lg bg-teal-500/10 p-2">
                <MapPin className="h-4 w-4 text-teal-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-3">
                <Select
                  value={selectedPais ?? '__todos__'}
                  onValueChange={(value) =>
                    handleFiltrarPais(value === '__todos__' ? null : value)
                  }
                >
                  <SelectTrigger className="w-full sm:w-[260px] h-9">
                    <SelectValue placeholder="Todos los países" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__todos__">Todos los países</SelectItem>
                    {[...paisDistribution]
                      .sort((a, b) => b.clics - a.clics)
                      .map((p) => (
                        <SelectItem key={p.nombre} value={p.nombre}>
                          {p.nombre} ({p.clics})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <WorldMap
                data={paisDistribution}
                selectedPais={selectedPais}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Selecciona un país en el filtro para ver sus resultados.
              </p>
            </CardContent>
          </Card>

          {/* Tablas de ubicación */}
          <div className="space-y-6">
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Por Ciudad</CardTitle>
                <div className="rounded-lg bg-teal-500/10 p-2">
                  <MapPin className="h-4 w-4 text-teal-500" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {data.clics_por_ciudad.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">Sin datos</p>
                ) : (
                  <div className="rounded-md border m-4 mt-0 max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)] sticky top-0 z-10">
                        <TableRow>
                          <TableHead>Ciudad</TableHead>
                          <TableHead className="text-right w-[60px]">Clics</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.clics_por_ciudad.map((item) => (
                          <TableRow key={item.nombre}>
                            <TableCell className="text-sm">{item.nombre}</TableCell>
                            <TableCell className="text-right font-medium text-sm">{item.clics}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Por Departamento</CardTitle>
                <div className="rounded-lg bg-teal-500/10 p-2">
                  <MapPin className="h-4 w-4 text-teal-500" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {data.clics_por_region.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">Sin datos</p>
                ) : (
                  <div className="rounded-md border m-4 mt-0 max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)] sticky top-0 z-10">
                        <TableRow>
                          <TableHead>Departamento</TableHead>
                          <TableHead className="text-right w-[60px]">Clics</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.clics_por_region.map((item) => (
                          <TableRow key={item.nombre}>
                            <TableCell className="text-sm">{item.nombre}</TableCell>
                            <TableCell className="text-right font-medium text-sm">{item.clics}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Por País</CardTitle>
                <div className="rounded-lg bg-teal-500/10 p-2">
                  <Globe className="h-4 w-4 text-teal-500" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {data.clics_por_pais.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">Sin datos</p>
                ) : (
                  <div className="rounded-md border m-4 mt-0 max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)] sticky top-0 z-10">
                        <TableRow>
                          <TableHead>País</TableHead>
                          <TableHead className="text-right w-[60px]">Clics</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.clics_por_pais.map((item) => (
                          <TableRow key={item.nombre}>
                            <TableCell className="text-sm">{item.nombre}</TableCell>
                            <TableCell className="text-right font-medium text-sm">{item.clics}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Clics Recientes */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-3">
            Clics Recientes
            <Badge className="badge-gradient hover:opacity-90">
              {data.clics_recientes.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.clics_recientes.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No hay clics registrados
            </p>
          ) : (
            <>
              <div className="rounded-md border m-4 mt-0">
                <Table>
                  <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
                    <TableRow>
                      <TableHead><div className="flex items-center gap-2"><Calendar className="h-4 w-4" />Fecha</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><User className="h-4 w-4" />Usuario</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Smartphone className="h-4 w-4" />Dispositivo</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Globe className="h-4 w-4" />Navegador</div></TableHead>
                      {data.url.habilitar_qr && (
                        <>
                          <TableHead><div className="flex items-center gap-2"><QrCode className="h-4 w-4" />Origen</div></TableHead>
                          <TableHead><div className="flex items-center gap-2"><MapPin className="h-4 w-4" />Ubicación</div></TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.clics_recientes
                      .slice((clicsPage - 1) * clicsPageSize, clicsPage * clicsPageSize)
                      .map((clic) => (
                        <TableRow key={clic.id}>
                          <TableCell className="text-muted-foreground">
                            {new Date(clic.fecha_clic).toLocaleString('es-EC', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {clic.numero_destinatario || clic.ip_address || 'Anónimo'}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              clic.dispositivo.toLowerCase().includes('mobile') || clic.dispositivo.toLowerCase().includes('móvil') || clic.dispositivo.toLowerCase().includes('movil')
                                ? 'badge-gradient hover:opacity-90'
                                : 'badge-gradient hover:opacity-90'
                            }>{clic.dispositivo}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {clic.navegador}
                          </TableCell>
                          {data.url.habilitar_qr && (
                            <>
                              <TableCell>
                                <Badge className={clic.origen === 'qr' ? 'badge-gradient hover:opacity-90' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-100'}>
                                  {clic.origen === 'qr' ? 'QR' : 'Link'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-xs">
                                {[clic.ciudad, clic.region].filter(Boolean).join(', ') || '—'}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 pb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Mostrar</span>
                  <Select
                    value={clicsPageSize.toString()}
                    onValueChange={(value) => {
                      setClicsPageSize(parseInt(value))
                      setClicsPage(1)
                    }}
                  >
                    <SelectTrigger className="w-[80px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">por página</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setClicsPage(1)}
                    disabled={clicsPage === 1}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setClicsPage(clicsPage - 1)}
                    disabled={clicsPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                    {clicsPage} de {Math.ceil(data.clics_recientes.length / clicsPageSize)}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setClicsPage(clicsPage + 1)}
                    disabled={clicsPage >= Math.ceil(data.clics_recientes.length / clicsPageSize)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setClicsPage(Math.ceil(data.clics_recientes.length / clicsPageSize))}
                    disabled={clicsPage >= Math.ceil(data.clics_recientes.length / clicsPageSize)}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Información adicional */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg">Información de la URL</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Código</span>
              <p className="font-mono font-medium">{data.url.codigo}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Canal</span>
              <p className="capitalize">{data.url.canal}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Campaña</span>
              <p>{data.url.campana || '-'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Creada</span>
              <p>{new Date(data.url.created_at).toLocaleDateString('es-EC')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
